import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { RootState } from '../../../store';
import {
  fetchDeviceFaults,
  deleteDeviceFaults,
} from '../../../store/devices/actionCreators';

interface DeviceFaultsProps {
  deviceId: number;
  onClose: () => void;
}

const StyledHeadCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: theme.palette.grey[100],
  borderBottom: `2px solid ${theme.palette.divider}`,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  zIndex: 1,
}));

const StyledBodyCell = styled(TableCell)({
  fontSize: '0.875rem',
  padding: '12px 16px',
  whiteSpace: 'nowrap',
});

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getDisplayValue = (value: unknown, fallback = '-'): string => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return fallback;
  }
};

const DeviceFaults: React.FC<DeviceFaultsProps> = ({ deviceId, onClose }) => {
  const dispatch = useDispatch();
  const { deviceFaults, isFaultsLoading, isFaultsDeleting } = useSelector(
    (state: RootState) => state.devices,
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    dispatch(fetchDeviceFaults(deviceId) as any);
  }, [dispatch, deviceId]);

  const handleRefresh = () => {
    dispatch(fetchDeviceFaults(deviceId) as any);
  };

  const handleDeleteFaults = () => {
    setShowDeleteConfirm(false);
    dispatch(deleteDeviceFaults(deviceId) as any);
  };

  const faults = deviceFaults || [];
  const unresolvedFaults = faults.filter(
    (f) => !f.cleared_at && !f.auto_cleared_at,
  );

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            height: '85vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'primary.contrastText',
            py: 2,
            px: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <WarningAmberIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Device Faults
            </Typography>
            {!isFaultsLoading && (
              <Chip
                label={`${unresolvedFaults.length} unresolved`}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'inherit',
                  fontWeight: 500,
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {unresolvedFaults.length > 0 && (
              <Tooltip title="Delete all unresolved faults">
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={
                    isFaultsDeleting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <DeleteSweepIcon />
                    )
                  }
                  disabled={isFaultsDeleting}
                  onClick={() => setShowDeleteConfirm(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    bgcolor: 'rgba(255,255,255,0.15)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                  }}
                >
                  Delete Unresolved ({unresolvedFaults.length})
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Refresh">
              <IconButton
                onClick={handleRefresh}
                size="small"
                sx={{ color: 'inherit' }}
                disabled={isFaultsLoading}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton
                onClick={onClose}
                size="small"
                sx={{ color: 'inherit' }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflow: 'auto', flex: 1 }}>
          {isFaultsLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 10,
              }}
            >
              <CircularProgress size={32} />
              <Typography sx={{ ml: 2 }} color="text.secondary">
                Loading faults...
              </Typography>
            </Box>
          ) : unresolvedFaults.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                All faults resolved
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This device has no unresolved faults.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <StyledHeadCell>Fault ID</StyledHeadCell>
                    <StyledHeadCell>Fault Code ID</StyledHeadCell>
                    <StyledHeadCell>Fault Type</StyledHeadCell>
                    <StyledHeadCell>Error ID</StyledHeadCell>
                    <StyledHeadCell>Error Detail</StyledHeadCell>
                    <StyledHeadCell>Created At</StyledHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unresolvedFaults.map((fault) => (
                    <TableRow
                      key={fault.fault_id}
                      hover
                      sx={{
                        '&:last-child td': { borderBottom: 0 },
                        transition: 'background-color 0.15s',
                      }}
                    >
                      <StyledBodyCell>{fault.fault_id}</StyledBodyCell>
                      <StyledBodyCell>
                        {fault.device_fault_code_id}
                      </StyledBodyCell>
                      <StyledBodyCell>
                        <Chip
                          label={getDisplayValue(fault.fault_type).replace(
                            /_/g,
                            ' ',
                          )}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 500,
                            textTransform: 'capitalize',
                          }}
                        />
                      </StyledBodyCell>
                      <StyledBodyCell>
                        {getDisplayValue(fault.payload?.error_id)}
                      </StyledBodyCell>
                      <StyledBodyCell>
                        <Typography
                          variant="body2"
                          sx={{ textTransform: 'capitalize' }}
                        >
                          {getDisplayValue(fault.payload?.error_detail)}
                        </Typography>
                      </StyledBodyCell>
                      <StyledBodyCell>
                        {formatDate(fault.created_at)}
                      </StyledBodyCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        PaperProps={{ sx: { borderRadius: 3, px: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Delete Unresolved Faults
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete all <strong>{unresolvedFaults.length}</strong>{' '}
            unresolved fault(s) for this device. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2, pr: 3 }}>
          <Button
            onClick={() => setShowDeleteConfirm(false)}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteFaults}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Delete All Unresolved
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeviceFaults;
